import { Component } from '@theme/component';
import { prefersReducedMotion, mediaQueryLarge } from '@theme/utilities';

const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));

/**
 * New Arrivals horizontal scroll.
 *
 * Pins a full-height stage while the page scrolls vertically and translates a
 * horizontal product track in lockstep, so vertical scroll reads as sideways
 * motion (like aliciamoore.com/about). The outer element is given a tall height
 * equal to the sticky stage plus the track's horizontal overflow, so 1px of
 * vertical scroll maps to 1px of horizontal travel (scaled by `speed`).
 *
 * On touch / small screens and for reduced-motion users it disables the pin and
 * falls back to a native horizontal scroll container — no scroll-jacking.
 */
class NewArrivalsScroll extends Component {
  requiredRefs = ['sticky', 'track', 'viewport'];

  #onScroll = null;
  #onResize = null;
  #observer = null;
  #rafId = 0;
  #ticking = false;
  #distance = 0;
  #lastP = -1;
  #enabled = false;
  #imgCleanup = [];

  connectedCallback() {
    super.connectedCallback();

    this.#onScroll = () => this.#requestUpdate();
    this.#onResize = () => this.#refresh();

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.toggleAttribute('data-onscreen', entry.isIntersecting);
        if (entry.isIntersecting && this.#enabled) {
          window.addEventListener('scroll', this.#onScroll, { passive: true });
          this.#requestUpdate();
        } else {
          window.removeEventListener('scroll', this.#onScroll);
        }
      },
      { rootMargin: '15% 0% 15% 0%' }
    );
    this.#observer.observe(this);

    window.addEventListener('resize', this.#onResize);
    mediaQueryLarge.addEventListener('change', this.#onResize);

    // Track width depends on images that may still be loading.
    this.#watchImages();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
    if (this.#onResize) {
      window.removeEventListener('resize', this.#onResize);
      mediaQueryLarge.removeEventListener('change', this.#onResize);
    }
    this.#imgCleanup.forEach((fn) => fn());
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  #watchImages() {
    const images = this.refs.track.querySelectorAll('img');
    images.forEach((img) => {
      if (img.complete) return;
      const handler = () => this.#refresh();
      img.addEventListener('load', handler, { once: true });
      this.#imgCleanup.push(() => img.removeEventListener('load', handler));
    });
  }

  /** Decide whether the pin effect should run, then (re)measure or tear down. */
  #refresh() {
    const shouldEnable = mediaQueryLarge.matches && !prefersReducedMotion();

    if (!shouldEnable) {
      this.#enabled = false;
      this.toggleAttribute('data-static', true);
      this.style.height = '';
      this.refs.track.style.transform = '';
      window.removeEventListener('scroll', this.#onScroll);
      return;
    }

    this.#enabled = true;
    this.removeAttribute('data-static');
    this.#measure();

    if (this.hasAttribute('data-onscreen')) {
      window.addEventListener('scroll', this.#onScroll, { passive: true });
    }
    this.#lastP = -1;
    this.#requestUpdate();
  }

  #measure() {
    const overflow = Math.max(0, this.refs.track.scrollWidth - this.refs.viewport.clientWidth);
    const speed = parseFloat(this.dataset.speed || '1') || 1;
    const stickyHeight = this.refs.sticky.offsetHeight;

    this.#distance = overflow;
    this.style.height = `${stickyHeight + overflow * speed}px`;
    this.style.setProperty('--nas-distance', `${overflow}px`);
  }

  #requestUpdate() {
    if (this.#ticking) return;
    this.#ticking = true;
    this.#rafId = requestAnimationFrame(() => {
      this.#ticking = false;
      this.#update();
    });
  }

  #update() {
    if (!this.#enabled) return;

    const total = this.offsetHeight - this.refs.sticky.offsetHeight;
    if (total <= 0) return;

    const rect = this.getBoundingClientRect();
    const p = clamp(-rect.top / total);
    if (Math.abs(p - this.#lastP) < 0.0005) return;
    this.#lastP = p;

    this.refs.track.style.transform = `translate3d(${-(this.#distance * p)}px, 0, 0)`;

    const bar = this.refs.progressBar;
    if (bar instanceof HTMLElement) {
      bar.style.transform = `scaleX(${p})`;
    }
  }
}

if (!customElements.get('new-arrivals-scroll')) {
  customElements.define('new-arrivals-scroll', NewArrivalsScroll);
}
